import type {
  AttributesProfileUpdateProps,
  CheckIsCustomerProps,
  APICheckIsCustomerResponse,
  CustomerProps,
  ProfileProps,
  RegisterProps,
  SignInResponseProps,
  ResetPasswordError,
  ResetPasswordAPIResponse,
  UpdateDataProps,
} from './AuthRepository.types';
import type { AxiosInstance } from 'axios';
import { MenuVersionsProps } from '@/interface/Menu';
import { setupApiClient } from '@/libs/axios/api';
import { apiConfig } from '@/config/api';
import { authKeys } from '@/config/keys';
import { getCookie, type ParseCookiesContext } from '@/utils/cookies';

export class AuthRepository {
  private readonly api: AxiosInstance;

  constructor(ctx?: ParseCookiesContext) {
    this.api = setupApiClient(ctx);
  }

  async checkIsCustomerOnAPI(
    values: CheckIsCustomerProps
  ): Promise<APICheckIsCustomerResponse> {
    const { data } = await this.api.post('auth/v1/verify-login', values);
    return data;
  }

  async signIn(values: {
    identifier: string;
    password: string;
  }): Promise<SignInResponseProps> {
    const { data } = await this.api.post('auth/v1/login/password', values);
    return data;
  }

  async requestOtpCode(values: {
    identifier: string;
    via: string;
  }): Promise<{ code: string; message: string }> {
    const sendData = {
      identifier: values.identifier,
      otp_type: values.via,
    };

    const { data } = await this.api.post('auth/v1/send-otp', sendData);
    return data;
  }

  async verifyOtpCode(values: {
    identifier: string;
    token: string;
  }): Promise<SignInResponseProps> {
    const { data } = await this.api.post('auth/v1/login/otp', values);
    return data;
  }

  async resetPassword(values: {
    password: string;
    token: string;
    identifier: string;
  }): Promise<ResetPasswordAPIResponse> {
    try {
      await this.api.post('auth/v1/reset-password', {
        new_password: values.password,
        token: values.token,
        identifier: values.identifier,
      });

      return { status: true, message: 'Senha alterada com sucesso' };
    } catch (error) {
      return {
        status: false,
        message: 'Erro ao alterar senha',
        property: (error as ResetPasswordError)?.response?.data?.message?.[0]
          ?.property,
      };
    }
  }

  async getProfile() {
    const { data } = await this.api.get<CustomerProps>('app/v1/me');
    return data;
  }

  async getProfileAuth() {
    const { data } = await this.api.get<CustomerProps>('auth/v1/account');
    return data;
  }

  async updateMe(
    attributes: AttributesProfileUpdateProps
  ): Promise<ProfileProps> {
    const { data } = await this.api.put('/app/v1/me', attributes);
    return data;
  }

  async sendEmailLead(values: {
    url_origin: string;
    email: string;
  }): Promise<boolean> {
    const { data } = await this.api.post('app/v1/public/leads', values);
    return data;
  }

  async register(values: RegisterProps): Promise<SignInResponseProps> {
    const { data } = await this.api.post('auth/v1/account', values);
    return data;
  }

  async updateData(values: UpdateDataProps): Promise<SignInResponseProps> {
    const { data } = await this.api.put('auth/v1/account', values);
    return data;
  }

  async getMenuVersions(): Promise<MenuVersionsProps> {
    const authToken = getCookie(authKeys.authToken);
    const urlFetchMenu = authToken
      ? apiConfig?.routes?.menu?.getMenu
      : apiConfig?.routes?.menu?.getMenuPublic;

    const {
      data: { data: menu },
    } = await this.api.get(urlFetchMenu);

    return menu;
  }

  async refreshToken() {
    const { data } = await this.api.post('auth/v1/refresh-token');
    return data.access_token;
  }
}
